type StatusTone =
  | "upcoming"
  | "selling"
  | "soldout"
  | "completed"
  | "prelaunch";

const compactNumber = (n: number, divisor: number, suffix: string) => {
  const value = n / divisor;
  const digits = value >= 100 ? 0 : value >= 10 ? 1 : 2;
  return `${Number(value.toFixed(digits))}${suffix}`;
};

export function formatPriceShort(n: number | null | undefined): string {
  if (n == null) return "";
  if (Math.abs(n) >= 1_000_000) return `S$${compactNumber(n, 1_000_000, "M")}`;
  if (Math.abs(n) >= 1_000) return `S$${compactNumber(n, 1_000, "K")}`;
  return `S$${n.toLocaleString("en-SG", { maximumFractionDigits: 0 })}`;
}

export function formatPriceFull(n: number | null | undefined): string {
  return n == null ? "" : `S$${n.toLocaleString("en-SG", { maximumFractionDigits: 0 })}`;
}

export function formatPsf(n: number | null | undefined): string {
  return n == null ? "" : `S$${n.toLocaleString("en-SG", { maximumFractionDigits: 0 })} psf`;
}

export function formatArea(n: number | null | undefined): string {
  return n == null ? "" : `${n.toLocaleString("en-SG", { maximumFractionDigits: 0 })} sqft`;
}

export function statusMeta(
  constructionStatus: string,
  isPreLaunch?: boolean
): { label: string; tone: StatusTone } {
  if (isPreLaunch) return { label: "Pre-launch", tone: "prelaunch" };

  switch (constructionStatus) {
    case "Upcoming":
      return { label: "Upcoming", tone: "upcoming" };
    case "Under Construction":
      return { label: "Now selling", tone: "selling" };
    case "Sold Out":
      return { label: "Sold out", tone: "soldout" };
    case "Move In":
    case "Completed":
      return { label: "Move in", tone: "completed" };
    default:
      return { label: constructionStatus, tone: "upcoming" };
  }
}

export function tenureLabel(
  isLeasehold: boolean,
  leaseYear?: number | null
): string {
  if (!isLeasehold) return "Freehold";
  return leaseYear === 999 ? "999-year" : "99-year leasehold";
}

/** Placeholder prices (S$1, S$1,250) sneak into min_flat_price. Treat anything under S$100,000 as unknown. */
export function sanePrice(n: number | null | undefined): number | null {
  if (n == null || Number.isNaN(n)) return null;
  return n >= 100_000 ? n : null;
}

/** "Dec 2028" / "2028-12-31" / "Q4 2029" -> "2028". */
export function topYear(value: string | null | undefined): string | null {
  if (!value) return null;
  const m = String(value).match(/(20\d{2})/);
  return m ? m[1] : null;
}

/** "One Marina Gardens 滨海花园一号" -> { latin: "One Marina Gardens", cjk: "滨海花园一号" } */
export function splitName(name: string): { latin: string; cjk: string | null } {
  const m = name.match(/^(.*?)\s*([㐀-鿿豈-﫿][^\n]*)$/);
  if (!m) return { latin: name.trim(), cjk: null };
  return { latin: m[1].trim() || name.trim(), cjk: m[2].trim() || null };
}
